import { useEffect, useRef, useState } from "react";
import { CustomDateRangePicker } from "../../../components/CustomDateRangePicker";
import moment from "moment";
import { dateToPosition } from "../../../utils/functionHelper";
import { getDashboardBooking, getDashboardBookingByLocationId } from "../../../services/Appointment";
import { CircularProgress, Divider } from "@mui/material";
import { FinancleCardPresentation } from "../../../components/FinancleCardPresentation";
import { CustomPieChart } from "../../../components/chart/piechart/CustomPieChart";
import { getLocation } from "../../../services/Location";

export const Charts = () => {
    const [locationId, setLocationId] = useState<number>(0);
    const [locationOptions, setLocationOptions] = useState<any[]>([]);
    const [showDateRangePicker, setShowDateRangePicker] = useState<boolean>(false);
    const [customStartDate, setCustomStartDate] = useState<string>(moment().startOf("month").subtract(1, 'month').format("MMM D, YYYY"));
    const [customEndDate, setCustomEndDate] = useState<string>(moment().format("MMM D, YYYY"));
    const [currentDateFormat, setCurrentDateFormat] = useState(
        customStartDate + " - " +
        customEndDate
    );
    const dateRangePickeref = useRef<HTMLDivElement | null>(null);
    const [dateType, setDateType] = useState<string>("Custom Range");
    const [isDataLoaded, setIsDataLoaded] = useState<boolean>(false);
    const [appointmentData, setAppointmentData] = useState<any>(null);
    const [financleData, setFinancleData] = useState<any[]>([]);
    const [bookedForDay, setBookedForDay] = useState<any[]>([])
    const [bookedForStatus, setBookedForStatus] = useState<any[]>([])
    const [bookedForService, setBookedForService] = useState<any[]>([])
    const [bookedForLocation, setBookedForLocation] = useState<any[]>([])

    useEffect(() => {
        const handleDropDown = (e: MouseEvent) => {
            if (!dateRangePickeref.current?.contains(e.target as Node)) setShowDateRangePicker(false);
        }

        document.addEventListener("click", handleDropDown);
        return () => document.removeEventListener("click", handleDropDown);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    useEffect(() => {
        getLocation()
            .then((data) => {
                setLocationOptions(() => data?.map((loc: any) => {
                    return {
                        value: loc?.id,
                        label: loc?.locationName
                    }
                }))

            })
            .catch((error) => {
                console.error(error)
            })
    }, [])


    useEffect(() => {
        if (locationId > 0 && dateType && customStartDate && customEndDate) {
            setIsDataLoaded(false);
            getDashboardBookingByLocationId(locationId, dateType, moment(customStartDate).format('YYYY-MM-DD'), moment(customEndDate).format('YYYY-MM-DD'))
                .then(data => {
                    setAppointmentData(data)
                    setIsDataLoaded(true);
                })
                .catch(error => console.error(error))
        }
        else if (locationId === 0 && dateType && customStartDate && customEndDate) {
            setIsDataLoaded(false);
            getDashboardBooking(dateType, moment(customStartDate).format('YYYY-MM-DD'), moment(customEndDate).format('YYYY-MM-DD'))
                .then(data => {
                    setAppointmentData(data)
                    setIsDataLoaded(true);
                })
                .catch(error => console.error(error))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customStartDate, customEndDate, locationId, dateType])

    useEffect(() => {
        if (appointmentData !== null) {
            const financleResults = [
                {
                    label: "Revenue",
                    symbol: "$",
                    count: appointmentData?.revenue,
                    vsLastMonthPercentage: appointmentData?.growthRevenueInPercentage,
                },
                {
                    label: "Amount Paid",
                    symbol: "$",
                    count: appointmentData?.amountPaid,
                    vsLastMonthPercentage: appointmentData?.growthAmountPaidInPercentage,
                },
                {
                    label: "Outstanding Balance",
                    count: appointmentData?.outstandingBalance,
                    symbol: "$",
                    vsLastMonthPercentage: appointmentData?.growthOutstandingBalanceInPercentage,
                },
                {
                    label: "Appointments",
                    count: appointmentData?.reservations,
                    vsLastMonthPercentage: appointmentData?.growthReservationsInPercentage,
                }
            ]
            setFinancleData(financleResults);
        }

    }, [appointmentData])

    useEffect(() => {
        if (appointmentData !== null) {
            setBookedForDay([
                { name: 'Sunday', value: appointmentData?.bookingsPerDay?.sunday },
                { name: 'Monday', value: appointmentData?.bookingsPerDay?.monday },
                { name: 'Tuesday', value: appointmentData?.bookingsPerDay?.tuesday },
                { name: 'Wednesday', value: appointmentData?.bookingsPerDay?.wednesday },
                { name: 'Thursday', value: appointmentData?.bookingsPerDay?.thursday },
                { name: 'Friday', value: appointmentData?.bookingsPerDay?.friday },
                { name: 'Saturday', value: appointmentData?.bookingsPerDay?.saturday }
            ]);
        }
    }, [appointmentData])

    useEffect(() => {
        if (appointmentData !== null) {
            setBookedForStatus(() => appointmentData?.bookingsPerStatuses?.map((item: any) => {
                return {
                    name: item?.name,
                    value: item?.count
                }
            })
            )
        }
    }, [appointmentData])

    useEffect(() => {
        if (appointmentData !== null) {
            setBookedForService(() => appointmentData?.bookingsPerServices?.map((item: any) => {
                return {
                    name: item?.name,
                    value: item?.count
                }
            })
            )
        }
    }, [appointmentData])

    useEffect(() => {
        if (appointmentData !== null) {
            setBookedForLocation(() => appointmentData?.bookingsPerLocations?.map((item: any) => {
                return {
                    name: item?.name,
                    value: item?.count
                }
            })
            )
        }
    }, [appointmentData])

    return (
        <div className={`px-[24px] py-[20px]`}>
            <div className="w-full h-full bg-white p-4 rounded-md">
                <div className="flex items-center justify-end py-2">

                    <div className="flex gap-2.5 items-center">
                        <div>
                            <select
                                value={locationId}
                                onChange={(e) => setLocationId(Number(e.target.value))}
                                className="w-[170px] border border-gray-300 rounded-md p-3 text-sm outline-none focus:border-primary bg-white"
                            >
                                <option value={0}>
                                    All
                                </option>
                                {locationOptions.map((loc: any, index: number) => (
                                    <option key={index} value={loc?.value}>
                                        {loc?.label}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div ref={dateRangePickeref}>
                            <div
                                className={`px-[4px] py-[10px] rounded-lg relative border border-gray-300 flex items-center gap-1.5 cursor-pointer`}
                                onClick={() => setShowDateRangePicker(!showDateRangePicker)}
                            >
                                <span className="text-xs text-nowrap font-medium">
                                    {currentDateFormat}
                                </span>
                                <span className="material-symbols-outlined text-[25px] font-extralight">
                                    date_range
                                </span>

                            </div>
                            <CustomDateRangePicker
                                zIndex={156}
                                setDateType={setDateType}
                                intialStartDate={customStartDate}
                                intialEndDate={customEndDate}
                                showDateRangePicker={showDateRangePicker}
                                setShowDateRangePicker={setShowDateRangePicker}
                                setDateRange={(startDate, endDate) => {
                                    setCustomStartDate(startDate);
                                    setCustomEndDate(endDate);
                                    const differentInDays = dateToPosition(moment(startDate), moment(endDate));
                                    if (differentInDays <= 0) {
                                        setCurrentDateFormat(
                                            moment(startDate).format('MMM D, YYYY')
                                        );
                                    } else {
                                        setCurrentDateFormat(
                                            moment(startDate).format('MMM D, YYYY') +
                                            moment(endDate).format(' - MMM D, YYYY')
                                        );
                                    }
                                }}
                            />
                        </div>
                    </div>
                </div>
                <>
                    {
                        !isDataLoaded ? (
                            <div className="flex h-[300px] items-center justify-center">
                                <CircularProgress />
                            </div>
                        )
                            : (
                                <>
                                    <div className="w-full mt-8 mb-4 ml-[10px]">
                                        <FinancleCardPresentation data={financleData} dateType={dateType} />
                                    </div>
                                    <Divider className={`w-full border-[#D0D0D0]`} />
                                    <div className="w-full flex flex-wrap">
                                        {
                                            bookedForDay.length > 0 && (
                                                <CustomPieChart
                                                    data={bookedForDay}
                                                    title="Appointments Per Day"
                                                    legendsFor="Appointments"
                                                />

                                            )
                                        }

                                        {
                                            bookedForStatus.length > 0 && (
                                                <CustomPieChart
                                                    data={bookedForStatus}
                                                    title="Appointments Per Status"
                                                    legendsFor="Appointments"
                                                />
                                            )
                                        }


                                        {
                                            bookedForService.length > 0 && (
                                                <CustomPieChart
                                                    data={bookedForService}
                                                    title="Appointments Per Service"
                                                    legendsFor="Appointments"
                                                />
                                            )
                                        }


                                        {
                                            bookedForLocation.length > 0 && (
                                                <CustomPieChart
                                                    data={bookedForLocation}
                                                    title="Appointments Per Location"
                                                    legendsFor="Appointments"
                                                />
                                            )
                                        }

                                    </div>
                                </>
                            )
                    }

                </>
            </div>
        </div>
    )
}